/**
 * The control plane's own client.
 *
 * Deliberately separate from `@/lib/api`: that one refreshes a customer
 * session and redirects to the customer sign-in page. Sharing it would mean a
 * platform administrator whose session lapsed could be bounced to a customer
 * login form, and a bug in either would be a bug in both.
 *
 * The token is held in memory only. A control-plane session reaches every
 * customer's data, and is not worth leaving in localStorage for anything that
 * can read the page.
 */
const BASE = "/api/platform";

export class PlatformError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

let token: string | null = null;
let onSignedOut: (() => void) | null = null;

export function setToken(next: string | null) {
  token = next;
}

export function onUnauthorized(handler: () => void) {
  onSignedOut = handler;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body) headers["content-type"] = "application/json";
  if (token) headers.authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...options, headers, credentials: "same-origin",
    });
  } catch {
    throw new PlatformError(0, "offline", "No connection to the server.");
  }

  if (response.status === 401) {
    token = null;
    onSignedOut?.();
  }
  if (!response.ok) {
    let code = "error";
    let message = "Something went wrong.";
    try {
      const body = await response.json();
      code = body?.error?.code ?? code;
      message = body?.error?.message ?? message;
    } catch {
      /* a non-JSON error body stays generic */
    }
    throw new PlatformError(response.status, code, message);
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/** Restore a session from the httpOnly refresh cookie.
 *
 *  The access token is deliberately never stored anywhere a script can read it,
 *  which used to mean a page reload was a sign-out. The cookie survives the
 *  reload and this exchanges it, so the security property is kept and the
 *  behaviour stops being hostile. `credentials: "same-origin"` matters: without
 *  it the cookie is not sent at all.
 */
export async function restore(): Promise<Admin | null> {
  try {
    const response = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "same-origin",
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { access_token: string; admin: Admin };
    token = body.access_token;
    return body.admin;
  } catch {
    return null;
  }
}

export const platform = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
};

export interface Licence {
  state: "active" | "expiring" | "grace" | "expired" | "suspended";
  expires_at: string | null;
  days_remaining: number | null;
  may_book: boolean;
  message: string;
}

export interface Customer {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  term_years: number | null;
  created_at: string | null;
  licence: Licence;
}

export interface Invitation {
  id: string;
  email: string;
  state: "pending" | "redeemed" | "revoked" | "expired";
  expires_at: string;
  sent_count: number;
  last_sent_at: string | null;
  /** Only ever present on the call that minted it. A read never returns one. */
  link?: string;
}

export interface CustomerDetail extends Customer {
  renews_at: string | null;
  usage: { rooms: number; users: number; devices: number; bookings_30d: number };
  payments: {
    id: string; amount: number; currency: string; paid_on: string;
    reference: string | null; covers_from: string | null;
    covers_until: string | null; note: string | null;
  }[];
  support_sessions: {
    id: string; reason: string; expires_at: string; ended_at: string | null;
  }[];
  invitation: Invitation | null;
}

export interface Health {
  checked_at: string;
  quiet: boolean;
  current_release: string | null;
  failing_integrations: {
    organization: string; provider: string; consecutive_failures: number;
    last_error: string; last_success_at: string | null;
  }[];
  silent_tablets: {
    organization: string; device: string; room: string | null;
    last_seen_at: string | null; never_seen: boolean;
  }[];
  expiring_subscriptions: {
    organization: string; provider: string; expires_at: string;
    awaiting_recreation: boolean;
  }[];
  tablets_behind: { organization: string; device: string; app_version: string }[];
}

export interface Admin {
  id: string;
  email: string;
  display_name: string;
  can_manage: boolean;
  must_change_password: boolean;
}

export interface NewCustomer extends Customer {
  owner: { id: string; email: string; name: string };
  /** Carries the link, and only here. Whoever holds it can set the owner's password. */
  invitation: Invitation;
}

export interface SmtpSettings {
  configured: boolean;
  /** Which won: what was saved here, or what the server's .env says. */
  source: "database" | "environment";
  host: string | null;
  port: number;
  username: string | null;
  password_set: boolean;
  from_address: string;
  starttls: boolean;
  updated_at: string | null;
  updated_by: string | null;
  /** Whether the bounce endpoint has ever heard from Amazon. */
  feedback: {
    last_seen_at: string | null;
    last_kind: string | null;
    messages: number;
    /** Amazon reached us, but from a topic SES_TOPIC_ARN does not name. */
    rejected: boolean;
    endpoint: string;
    topic_locked: boolean;
    /** SES_TOPIC_ARN as the running server has it. */
    topic_arn: string | null;
    /** Set, but not shaped like arn:aws:sns:<region>:<account>:<name>. */
    topic_malformed: boolean;
    /** The topic Amazon named on the last refused message. */
    refused_topic: string | null;
  };
}

export interface StaffMember {
  id: string;
  email: string;
  display_name: string;
  can_manage: boolean;
  active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  signed_in_now: boolean;
  created_at: string | null;
}

/** A create or a reset. The password appears once and is never readable again. */
export interface StaffWithPassword extends StaffMember {
  password: string;
}

export interface NotificationEvent {
  key: string;
  label: string;
  description: string;
  /** Whether Alphabeta allows it. A customer can still switch it off. */
  enabled: boolean;
}

export interface SmtpTestResult {
  ok: boolean;
  detail: string;
}
